import { stringify } from 'csv-stringify';
import AdmZip from 'adm-zip';
import { uzipGtfsData } from '../libs/gtfs-lib';
import {parse} from 'csv-parse/dist/esm/sync';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import isBetween from 'dayjs/plugin/isBetween';
import { keyBy } from 'lodash';

// Extend dayjs with plugins
dayjs.extend(minMax);
dayjs.extend(isBetween);

export class GTFSProcessor {
    constructor() {
        this.data = {
            agency: [],
            routes: [],
            trips: [],
            stopTimes: [],
            stops: [],
            calendar: [],
            calendarDates: [],
            shapes: [],
            routeVariants: [],
            deadruns: [],
            shifts: []
        };
        this.loaded = false;
    }

    async loadGTFSFile(file) {
        try {
            console.log('Starting to load GTFS file:', file.name);
            const arrayBuffer = await file.arrayBuffer();
            const buffer = Buffer.from(arrayBuffer);
            const gtfsData = uzipGtfsData(buffer, parse);

            console.log('gtfsData', gtfsData);
            
            // Map network_id to contractId for each route
            const routes = gtfsData.routes.map(route => ({
                ...route,
                contractId: route.network_id || route.agency_id || ''
            }));

            this.data = {
                agency: gtfsData.agency || [],
                routes: routes || [],
                trips: gtfsData.trips || [],
                stopTimes: gtfsData.stopTimes || [],
                stops: gtfsData.stops || [],
                calendar: gtfsData.calendars || [],
                calendarDates: gtfsData.calendarDates || [],
                shapes: gtfsData.shapes || [],
                routeVariants: gtfsData.routeVariants || [],
                deadruns: gtfsData.deadruns || [],
                shifts: gtfsData.shifts || []
            };

            this.loaded = true;
            return this.getDateRange();
        } catch (error) {
            console.error('Error loading GTFS file:', error);
            throw error;
        }
    }

    getDateRange() {
        if (!this.data.calendar || this.data.calendar.length === 0) {
            return null;
        }

        const dates = this.data.calendar.map(entry => ({
            start: dayjs(entry.start_date, 'YYYYMMDD'),
            end: dayjs(entry.end_date, 'YYYYMMDD')
        }));

        const startDate = dayjs.min(dates.map(d => d.start));
        const endDate = dayjs.max(dates.map(d => d.end));

        return {
            start: startDate.format('YYYY-MM-DD'),
            end: endDate.format('YYYY-MM-DD')
        };
    }

    // Helper to calculate distance between two stops
    calculateDistance(stop1, stop2) {
        const R = 6371; // Earth's radius in km
        const lat1 = parseFloat(stop1.stop_lat);
        const lon1 = parseFloat(stop1.stop_lon);
        const lat2 = parseFloat(stop2.stop_lat);
        const lon2 = parseFloat(stop2.stop_lon);

        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
                Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
    }

    // Helper to check if a service runs on a given date
    doesServiceRunOnDate(service, date) {
        // First check if date is within service's date range
        const serviceStart = dayjs(service.start_date, 'YYYYMMDD').startOf('day');
        const serviceEnd = dayjs(service.end_date, 'YYYYMMDD').endOf('day');
        const checkDate = date.startOf('day');
        
        if (!checkDate.isBetween(serviceStart, serviceEnd, 'day', '[]')) {
            return false;
        }

        const dayOfWeek = checkDate.day();
        const dayMap = {
            0: 'sunday',
            1: 'monday',
            2: 'tuesday',
            3: 'wednesday',
            4: 'thursday',
            5: 'friday',
            6: 'saturday'
        };

        // Check calendar_dates for exceptions
        const dateStr = checkDate.format('YYYYMMDD');
        const exception = this.data.calendarDates.find(cd => 
            cd.service_id === service.service_id && 
            cd.date === dateStr
        );

        if (exception) {
            // 1 = service added, 2 = service removed
            return exception.exception_type === '1';
        }

        // Check regular calendar
        return service[dayMap[dayOfWeek]] === '1';
    }

    // Calculate distance using shape points if available, otherwise use stops
    calculateTripDistance(trip, stopTimes) {
        // Get route variant distance from route_variants.txt
        const routeVariant = this.data.routeVariants.find(rv => rv.route_variant_id === trip.route_variant_id);
        if (routeVariant) {
            return parseFloat(routeVariant.route_variant_distance_km);
        }
        return 0;
    }

    // Calculate trip duration in hours (first stop departure to last stop arrival)
    calculateTripDuration(trip, stopTimes) {
        if (!stopTimes || stopTimes.length < 2) {
            return 0;
        }

        // Sort stop times by sequence
        const sortedStopTimes = stopTimes.sort((a, b) => parseInt(a.stop_sequence) - parseInt(b.stop_sequence));
        
        // Get first and last stop times
        const firstStop = sortedStopTimes[0];
        const lastStop = sortedStopTimes[sortedStopTimes.length - 1];
        
        // Get departure time from first stop and arrival time from last stop
        let departureTime = firstStop.departure_time || firstStop.arrival_time;
        let arrivalTime = lastStop.arrival_time || lastStop.departure_time;
        
        if (!departureTime || !arrivalTime) {
            return 0;
        }

        // Convert GTFS time format (HH:MM:SS) to hours
        const departureHours = this.convertGTFSTimeToHours(departureTime);
        const arrivalHours = this.convertGTFSTimeToHours(arrivalTime);
        
        // Handle trips that span midnight (arrival time is less than departure time)
        let duration = arrivalHours - departureHours;
        if (duration < 0) {
            duration += 24; // Add 24 hours for trips spanning midnight
        }
        
        return duration;
    }

    // Convert GTFS time format (HH:MM:SS) to decimal hours
    convertGTFSTimeToHours(gtfsTime) {
        if (!gtfsTime) return 0;
        
        const parts = gtfsTime.split(':').map(Number);
        if (parts.length < 2) return 0;
        
        const hours = parts[0];
        const minutes = parts[1] || 0;
        const seconds = parts[2] || 0;
        
        return hours + (minutes / 60) + (seconds / 3600);
    }

    // Calculate service kilometers for a route in a given month
    calculateRouteKilometers(routeId, month) {
        const trips = this.data.trips.filter(trip => trip.route_id === routeId);
        let totalKm = 0;

        trips.forEach(trip => {
            const service = this.data.calendar.find(s => s.service_id === trip.service_id);
            if (!service) return;

            // Get stop times for this trip
            const stopTimes = this.data.stopTimes
                .filter(st => st.trip_id === trip.trip_id)
                .sort((a, b) => parseInt(a.stop_sequence) - parseInt(b.stop_sequence));

            // Calculate trip distance using shape points
            const tripDistance = this.calculateTripDistance(trip, stopTimes);

            // Count how many times this service runs in the month
            const daysInMonth = month.daysInMonth();
            let serviceCount = 0;
            for (let day = 1; day <= daysInMonth; day++) {
                const date = month.date(day);
                if (this.doesServiceRunOnDate(service, date)) {
                    serviceCount++;
                }
            }

            totalKm += tripDistance * serviceCount;
        });

        return totalKm;
    }

    // Calculate service hours for a route in a given month
    calculateRouteHours(routeId, month) {
        const trips = this.data.trips.filter(trip => trip.route_id === routeId);
        let totalHours = 0;

        trips.forEach(trip => {
            const service = this.data.calendar.find(s => s.service_id === trip.service_id);
            if (!service) return;

            // Get stop times for this trip
            const stopTimes = this.data.stopTimes
                .filter(st => st.trip_id === trip.trip_id)
                .sort((a, b) => parseInt(a.stop_sequence) - parseInt(b.stop_sequence));

            // Calculate trip duration in hours
            const tripDuration = this.calculateTripDuration(trip, stopTimes);

            // Count how many times this service runs in the month
            const daysInMonth = month.daysInMonth();
            let serviceCount = 0;
            for (let day = 1; day <= daysInMonth; day++) {
                const date = month.date(day);
                if (this.doesServiceRunOnDate(service, date)) {
                    serviceCount++;
                }
            }

            totalHours += tripDuration * serviceCount;
        });

        return totalHours;
    }

    // Calculate monthly service statistics
    calculateMonthlyServiceStats(startDate, endDate) {
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const months = [];
        let current = start.startOf('month');

        // Pre-process trips by route
        const tripsByRoute = {};
        this.data.trips.forEach(trip => {
            if (!tripsByRoute[trip.route_id]) {
                tripsByRoute[trip.route_id] = [];
            }
            tripsByRoute[trip.route_id].push(trip);
        });

        // Pre-process stop times by trip
        const stopTimesByTrip = {};
        this.data.stopTimes.forEach(st => {
            if (!stopTimesByTrip[st.trip_id]) {
                stopTimesByTrip[st.trip_id] = [];
            }
            stopTimesByTrip[st.trip_id].push(st);
        });

        // Pre-process stops lookup
        const stopsById = {};
        this.data.stops.forEach(stop => {
            stopsById[stop.stop_id] = stop;
        });

        // Pre-process agency lookup
        const agencyById = {};
        this.data.agency.forEach(agency => {
            agencyById[agency.agency_id] = agency;
        });

        while (current.isBefore(end)) {
            const monthData = {
                month: current.format('YYYY-MM'),
                routeKilometers: {},
                routeHours: {},
                totalTrips: 0,
                servicesByRoute: {},
                tripsByDay: {},
                agencyStats: {}
            };

            // Process each route
            this.data.routes.forEach(route => {
                const routeTrips = tripsByRoute[route.route_id] || [];
                let routeKm = 0;
                let routeHours = 0;
                let routeServices = new Set();
                let tripCount = 0;

                routeTrips.forEach(trip => {
                    const service = this.data.calendar.find(s => s.service_id === trip.service_id);
                    if (!service) return;

                    // Get stop times for this trip
                    const stopTimes = (stopTimesByTrip[trip.trip_id] || [])
                        .sort((a, b) => parseInt(a.stop_sequence) - parseInt(b.stop_sequence));

                    // Calculate trip distance using shapes if available
                    const tripDistance = this.calculateTripDistance(trip, stopTimes);
                    // Calculate trip duration in hours
                    const tripDuration = this.calculateTripDuration(trip, stopTimes);

                    // Count service days in this month
                    const daysInMonth = current.daysInMonth();
                    let serviceDaysInMonth = 0;
                    for (let day = 1; day <= daysInMonth; day++) {
                        const date = current.date(day);
                        if (this.doesServiceRunOnDate(service, date)) {
                            serviceDaysInMonth++;
                        }
                    }

                    // Add kilometers and hours for each service day
                    routeKm += tripDistance * serviceDaysInMonth;
                    routeHours += tripDuration * serviceDaysInMonth;
                    monthData.totalTrips += serviceDaysInMonth;
                    tripCount += serviceDaysInMonth;
                    routeServices.add(service.service_id);

                    // Track trips by day
                    for (let day = 1; day <= daysInMonth; day++) {
                        const date = current.date(day);
                        if (this.doesServiceRunOnDate(service, date)) {
                            const dayKey = date.format('YYYY-MM-DD');
                            if (!monthData.tripsByDay[dayKey]) {
                                monthData.tripsByDay[dayKey] = 0;
                            }
                            monthData.tripsByDay[dayKey]++;
                        }
                    }

                    // Track agency stats
                    if (route.agency_id && agencyById[route.agency_id]) {
                        const agencyId = route.agency_id;
                        if (!monthData.agencyStats[agencyId]) {
                            monthData.agencyStats[agencyId] = {
                                name: agencyById[agencyId].agency_name,
                                kilometers: 0,
                                hours: 0,
                                trips: 0,
                                routes: new Set()
                            };
                        }
                        monthData.agencyStats[agencyId].kilometers += tripDistance * serviceDaysInMonth;
                        monthData.agencyStats[agencyId].hours += tripDuration * serviceDaysInMonth;
                        monthData.agencyStats[agencyId].trips += serviceDaysInMonth;
                        monthData.agencyStats[agencyId].routes.add(route.route_id);
                    }
                });

                if (routeKm > 0) {
                    monthData.routeKilometers[route.route_id] = {
                        routeNumber: route.route_short_name || route.route_id,
                        kilometers: routeKm,
                        serviceCount: routeServices.size,
                        tripCount: tripCount,
                        agencyId: route.agency_id,
                        agencyName: route.agency_id ? agencyById[route.agency_id]?.agency_name : null
                    };
                }

                if (routeHours > 0) {
                    monthData.routeHours[route.route_id] = {
                        routeNumber: route.route_short_name || route.route_id,
                        hours: routeHours,
                        serviceCount: routeServices.size,
                        tripCount: tripCount,
                        agencyId: route.agency_id,
                        agencyName: route.agency_id ? agencyById[route.agency_id]?.agency_name : null
                    };
                }
            });

            // Convert agency stats Sets to arrays for easier handling
            Object.values(monthData.agencyStats).forEach(stats => {
                stats.routes = stats.routes.size;
            });

            months.push(monthData);
            current = current.add(1, 'month');
        }

        return months;
    }

    generateOSR(params) {
        console.log('Generating OSR with params:', params);
        
        if (!this.loaded) {
            throw new Error('GTFS data not loaded. Please upload a valid GTFS file.');
        }

        try {
            const { startDate, endDate } = params;
            
            // Validate date range
            if (!startDate || !endDate) {
                throw new Error('Start and end dates are required for the report.');
            }
            
            // Check if we have valid service data for the date range
            const dateRange = this.getDateRange();
            if (!dateRange.start || !dateRange.end) {
                throw new Error('The GTFS data does not contain valid service dates.');
            }
            
            // Convert dates to dayjs objects for comparison
            const start = dayjs(startDate);
            const end = dayjs(endDate);
            const gtfsStart = dayjs(dateRange.start);
            const gtfsEnd = dayjs(dateRange.end);
            
            // Warn if date range is partially or completely outside GTFS coverage
            if (start.isBefore(gtfsStart) || end.isAfter(gtfsEnd)) {
                console.warn('Date range is partially outside GTFS data coverage:', {
                    requestedRange: { start: startDate, end: endDate },
                    gtfsDataRange: dateRange
                });
            }
            
            console.log('Calculating monthly statistics...');
            const monthlyStats = this.calculateMonthlyServiceStats(startDate, endDate);
            
            // If no monthly stats are returned, show a friendly error
            if (!monthlyStats || monthlyStats.length === 0) {
                throw new Error('No service data found for the selected date range. Try adjusting your dates.');
            }
            
            console.log('Generating report sections...');
            const routeSummary = this.generateRouteSummary(monthlyStats);
            const routeHoursSummary = this.generateRouteHoursSummary(monthlyStats);
            const tripSummary = this.generateTripSummary(monthlyStats, params);
            const agencySummary = this.generateAgencySummary(monthlyStats);
            const insights = this.generateInsights(monthlyStats);
            const deadrunSummary = this.generateDeadrunSummary(monthlyStats);
            
            // Calculate additional operational metrics
            const operationalMetrics = this.calculateOperationalMetrics(monthlyStats);
            
            console.log('OSR generation complete');
            return {
                monthlyStats,
                routeSummary,
                routeHoursSummary,
                tripSummary,
                agencySummary,
                insights,
                operationalMetrics,
                deadrunSummary,
                // Include date range information in the report
                reportInfo: {
                    generatedAt: new Date().toISOString(),
                    dateRange: { startDate, endDate },
                    gtfsDateRange: dateRange,
                    dataWarnings: this.getDataWarnings(startDate, endDate, dateRange)
                }
            };
        } catch (error) {
            console.error('Error generating OSR report:', error);
            throw new Error(`Failed to generate report: ${error.message}`);
        }
    }

    generateRouteSummary(monthlyStats) {
        const routeSummary = {};
        
        monthlyStats.forEach(monthData => {
            Object.entries(monthData.routeKilometers).forEach(([routeId, routeData]) => {
                if (!routeSummary[routeId]) {
                    // Get the route from GTFS data to get network_id
                    const route = this.data.routes.find(r => r.route_id === routeId);
                    routeSummary[routeId] = {
                        routeNumber: routeData.routeNumber,
                        networkId: route?.network_id || route?.contractId || '',
                        monthlyKilometers: [],
                        totalServices: 0
                    };
                }
                routeSummary[routeId].monthlyKilometers.push({
                    month: monthData.month,
                    kilometers: routeData.kilometers,
                    serviceCount: routeData.serviceCount
                });
                routeSummary[routeId].totalServices = Math.max(
                    routeSummary[routeId].totalServices,
                    routeData.serviceCount
                );
            });
        });

        return Object.values(routeSummary);
    }

    generateRouteHoursSummary(monthlyStats) {
        const routeSummary = {};
        
        monthlyStats.forEach(monthData => {
            Object.entries(monthData.routeHours || {}).forEach(([routeId, routeData]) => {
                if (!routeSummary[routeId]) {
                    // Get the route from GTFS data to get network_id
                    const route = this.data.routes.find(r => r.route_id === routeId);
                    routeSummary[routeId] = {
                        routeNumber: routeData.routeNumber,
                        networkId: route?.network_id || route?.contractId || '',
                        monthlyHours: [],
                        totalServices: 0
                    };
                }
                routeSummary[routeId].monthlyHours.push({
                    month: monthData.month,
                    hours: routeData.hours,
                    serviceCount: routeData.serviceCount
                });
                routeSummary[routeId].totalServices = Math.max(
                    routeSummary[routeId].totalServices,
                    routeData.serviceCount
                );
            });
        });

        return Object.values(routeSummary);
    }

    generateTripSummary(monthlyStats, params) {
        if (!monthlyStats || monthlyStats.length === 0) {
            return null;
        }

        // Original monthly overview data
        const summary = {
            totalTrips: 0,
            totalKilometers: 0,
            averageTripsPerDay: 0,
            tripsPerMonth: {}
        };

        // Get the service days to calculate averages
        let totalDays = 0;
        monthlyStats.forEach(month => {
            const [year, monthNum] = month.month.split('-');
            const daysInMonth = new Date(year, parseInt(monthNum), 0).getDate();
            totalDays += daysInMonth;
            
            // Add month data to summary
            summary.totalTrips += month.totalTrips || 0;
            summary.totalKilometers += month.totalKilometers || 0;
            summary.tripsPerMonth[month.month] = month.totalTrips || 0;
        });

        // Calculate average trips per day
        summary.averageTripsPerDay = totalDays > 0 ? Math.round(summary.totalTrips / totalDays) : 0;

        // Create a structure for route-based trip counts
        const routeTripCounts = [];
        const uniqueRoutes = new Set();
        
        // First, collect all unique routes across all months
        monthlyStats.forEach(month => {
            if (month.routeKilometers) {
                Object.keys(month.routeKilometers).forEach(routeId => {
                    uniqueRoutes.add(routeId);
                });
            }
        });
        
        // Then, initialize the route trip counts structure
        uniqueRoutes.forEach(routeId => {
            // Get route details from the first month that has this route
            let routeNumber = null;
            for (const month of monthlyStats) {
                if (month.routeKilometers && month.routeKilometers[routeId]) {
                    routeNumber = month.routeKilometers[routeId].routeNumber;
                    break;
                }
            }
            
            // Get the route from GTFS data to get network_id
            const route = this.data.routes.find(r => r.route_id === routeId);
            
            const routeData = {
                routeId,
                routeNumber: routeNumber || routeId,
                networkId: route?.network_id || route?.contractId || '',
                monthlyTripCounts: {}
            };
            
            // Initialize trip counts for each month
            monthlyStats.forEach(month => {
                routeData.monthlyTripCounts[month.month] = 0;
            });
            
            routeTripCounts.push(routeData);
        });
        
        // Populate trip counts from monthly stats
        monthlyStats.forEach(month => {
            if (month.routeKilometers) {
                Object.entries(month.routeKilometers).forEach(([routeId, routeData]) => {
                    // Find the corresponding route in our routeTripCounts array
                    const routeEntry = routeTripCounts.find(r => r.routeId === routeId);
                    if (routeEntry) {
                        // For each month, store the trip count for this route
                        routeEntry.monthlyTripCounts[month.month] = routeData.tripCount || 0;
                    }
                });
            }
        });
        
        // Add the route trip counts to the summary
        summary.routeTripCounts = routeTripCounts;

        return summary;
    }

    generateAgencySummary(monthlyStats) {
        const agencySummary = {};
        
        monthlyStats.forEach(month => {
            Object.entries(month.agencyStats).forEach(([agencyId, stats]) => {
                if (!agencySummary[agencyId]) {
                    agencySummary[agencyId] = {
                        name: stats.name,
                        monthlyStats: []
                    };
                }
                agencySummary[agencyId].monthlyStats.push({
                    month: month.month,
                    kilometers: stats.kilometers,
                    hours: stats.hours,
                    trips: stats.trips,
                    routes: stats.routes
                });
            });
        });

        return Object.values(agencySummary);
    }

    generateInsights(monthlyStats) {
        const insights = [];

        // Service Utilization
        const serviceUtilization = monthlyStats.map(month => {
            const [year, monthNum] = month.month.split('-');
            const daysInMonth = new Date(year, monthNum, 0).getDate();
            const activeDays = Object.values(month.routeKilometers).reduce((days, route) => 
                Math.max(days, route.serviceCount || 0), 0);
            
            return {
                month: month.month,
                utilization: ((activeDays / daysInMonth) * 100).toFixed(1)
            };
        });

        insights.push({
            title: 'Service Utilization',
            description: 'Percentage of days with active services per month',
            data: serviceUtilization.map(data => ({
                metric: data.month,
                value: `${data.utilization}%`
            }))
        });

        // Route Performance Analysis
        const routePerformance = {};
        monthlyStats.forEach(month => {
            Object.entries(month.routeKilometers).forEach(([routeId, stats]) => {
                if (!routePerformance[routeId]) {
                    routePerformance[routeId] = {
                        totalKm: 0,
                        totalServices: 0,
                        months: 0
                    };
                }
                routePerformance[routeId].totalKm += stats.kilometers || 0;
                routePerformance[routeId].totalServices += stats.serviceCount || 0;
                routePerformance[routeId].months += 1;
            });
        });

        const topRoutes = Object.entries(routePerformance)
            .map(([routeId, stats]) => ({
                routeId,
                avgKmPerMonth: stats.totalKm / stats.months,
                avgServicesPerMonth: stats.totalServices / stats.months
            }))
            .sort((a, b) => b.avgKmPerMonth - a.avgKmPerMonth)
            .slice(0, 5);

        insights.push({
            title: 'Top Performing Routes',
            description: 'Routes with highest average monthly service kilometers',
            data: topRoutes.map(route => ({
                metric: `Route ${route.routeId}`,
                value: `${Math.round(route.avgKmPerMonth).toLocaleString()} km/month`,
                subValue: `${Math.round(route.avgServicesPerMonth)} services/month`
            }))
        });

        // Monthly Trend Analysis
        const monthlyTrends = monthlyStats.map((month, index, array) => {
            const totalKm = Object.values(month.routeKilometers)
                .reduce((sum, route) => sum + (route.kilometers || 0), 0);
            const prevMonth = index > 0 ? array[index - 1] : null;
            const prevTotalKm = prevMonth ? Object.values(prevMonth.routeKilometers)
                .reduce((sum, route) => sum + (route.kilometers || 0), 0) : totalKm;
            const change = prevMonth ? ((totalKm - prevTotalKm) / prevTotalKm * 100).toFixed(1) : '0.0';

            return {
                month: month.month,
                totalKm,
                change
            };
        });

        insights.push({
            title: 'Monthly Service Growth',
            description: 'Month-over-month change in service kilometers',
            data: monthlyTrends.map(trend => ({
                metric: trend.month,
                value: `${trend.change}%`,
                subValue: `${Math.round(trend.totalKm).toLocaleString()} km total`
            }))
        });

        // Service Intensity Analysis
        const serviceIntensity = monthlyStats.map(month => {
            const [year, monthNum] = month.month.split('-');
            const daysInMonth = new Date(year, monthNum, 0).getDate();
            const totalKm = Object.values(month.routeKilometers)
                .reduce((sum, route) => sum + (route.kilometers || 0), 0);
            const avgKmPerDay = totalKm / daysInMonth;

            return {
                month: month.month,
                avgKmPerDay: Math.round(avgKmPerDay).toLocaleString()
            };
        });

        insights.push({
            title: 'Daily Service Intensity',
            description: 'Average service kilometers operated per day',
            data: serviceIntensity.map(data => ({
                metric: data.month,
                value: `${data.avgKmPerDay} km/day`
            }))
        });

        return insights;
    }

    // New method to check for data quality issues or warnings
    getDataWarnings(startDate, endDate, gtfsDateRange) {
        const warnings = [];
        
        // Check if date range falls outside GTFS data
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const gtfsStart = dayjs(gtfsDateRange.start);
        const gtfsEnd = dayjs(gtfsDateRange.end);
        
        if (start.isBefore(gtfsStart)) {
            warnings.push({
                type: 'dateRangeWarning',
                message: `The start date (${startDate}) is before the GTFS data start date (${gtfsDateRange.start}).`
            });
        }
        
        if (end.isAfter(gtfsEnd)) {
            warnings.push({
                type: 'dateRangeWarning',
                message: `The end date (${endDate}) is after the GTFS data end date (${gtfsDateRange.end}).`
            });
        }
        
        // Check for incomplete data
        if (this.data.routes.length === 0) {
            warnings.push({
                type: 'missingDataWarning',
                message: 'The GTFS data does not contain any routes.'
            });
        }
        
        if (this.data.trips.length === 0) {
            warnings.push({
                type: 'missingDataWarning',
                message: 'The GTFS data does not contain any trips.'
            });
        }
        
        if (this.data.stopTimes.length === 0) {
            warnings.push({
                type: 'missingDataWarning',
                message: 'The GTFS data does not contain any stop times.'
            });
        }
        
        return warnings;
    }

    // New method to calculate additional operational metrics
    calculateOperationalMetrics(monthlyStats) {
        const metrics = {
            averageServiceKilometers: 0,
            averageStopsPerTrip: 0,
            serviceEfficiency: 0,
            routeCoverage: 0,
            serviceDistribution: {}
        };
        
        try {
            let totalKilometers = 0;
            let totalServices = 0;
            let totalTrips = 0;
            let totalStops = 0;
            
            // Process each month's data
            monthlyStats.forEach(month => {
                // Extract route data
                Object.values(month.routeKilometers).forEach(route => {
                    totalKilometers += route.kilometers || 0;
                    totalServices += route.serviceCount || 0;
                });
                
                // Extract trip data if available
                if (month.tripData) {
                    Object.values(month.tripData).forEach(trip => {
                        totalTrips += trip.count || 0;
                        totalStops += trip.stopCount || 0;
                    });
                }
            });
            
            // Calculate averages
            metrics.averageServiceKilometers = totalServices > 0 
                ? totalKilometers / totalServices 
                : 0;
                
            metrics.averageStopsPerTrip = totalTrips > 0 
                ? totalStops / totalTrips 
                : 0;
                
            // Calculate service efficiency (higher is better)
            metrics.serviceEfficiency = totalKilometers > 0 && totalServices > 0
                ? totalTrips / totalKilometers
                : 0;
                
            // Calculate route coverage
            const uniqueRoutes = new Set();
            monthlyStats.forEach(month => {
                Object.keys(month.routeKilometers).forEach(routeId => {
                    uniqueRoutes.add(routeId);
                });
            });
            
            metrics.routeCoverage = uniqueRoutes.size;
            
            // Calculate service distribution by day of week
            const dayDistribution = {
                'Monday': 0,
                'Tuesday': 0,
                'Wednesday': 0,
                'Thursday': 0,
                'Friday': 0,
                'Saturday': 0,
                'Sunday': 0
            };
            
            monthlyStats.forEach(month => {
                if (month.dayOfWeekData) {
                    Object.entries(month.dayOfWeekData).forEach(([day, value]) => {
                        dayDistribution[day] = (dayDistribution[day] || 0) + value;
                    });
                }
            });
            
            metrics.serviceDistribution = dayDistribution;
            
            return metrics;
        } catch (error) {
            console.error('Error calculating operational metrics:', error);
            return metrics;
        }
    }

    // Calculate dead-run kilometers for a driver shift in a given month
    calculateDeadrunKilometers(shiftId, month) {
        // Find all dead-runs associated with this shift
        const deadruns = this.data.deadruns?.filter(dr => dr.shiftId === shiftId) || [];
        let totalKm = 0;
        
        // Get the shift to determine its service days
        const shift = this.data.shifts?.find(s => s.shift_id === shiftId);
        if (!shift) {
            console.warn(`No shift found for shiftId: ${shiftId}`);
            return 0;
        }
        
        // Get the service ID from the shift
        const serviceId = shift.service_id;
        if (!serviceId) {
            console.warn(`No service_id found for shift: ${shiftId}`);
            return 0;
        }
        
        // Find the calendar entry for this service
        const calendar = this.data.calendar?.find(c => c.service_id === serviceId);
        if (!calendar) {
            console.warn(`No calendar found for service: ${serviceId}`);
            return 0;
        }
        
        // Count how many days this service runs in the month
        const daysInMonth = month.daysInMonth();
        let serviceDaysInMonth = 0;
        
        // Create a map to track service days with exceptions
        const serviceDaysMap = {};
        
        // First, mark all days based on the regular calendar
        for (let day = 1; day <= daysInMonth; day++) {
            const date = month.date(day);
            const dayOfWeek = date.day(); // 0 = Sunday, 1 = Monday, etc.
            
            // Check if the service runs on this day of the week
            const dayMap = {
                0: 'sunday',
                1: 'monday',
                2: 'tuesday',
                3: 'wednesday',
                4: 'thursday',
                5: 'friday',
                6: 'saturday'
            };
            
            // Initialize the day in our map
            const dateStr = date.format('YYYYMMDD');
            serviceDaysMap[dateStr] = calendar[dayMap[dayOfWeek]] === '1';
        }
        
        // Then apply calendar date exceptions
        const exceptions = this.data.calendarDates?.filter(cd => 
            cd.service_id === serviceId && 
            month.isSame(dayjs(cd.date, 'YYYYMMDD'), 'month')
        ) || [];
        
        exceptions.forEach(exception => {
            const dateStr = exception.date;
            // 1 = service added, 2 = service removed
            if (exception.exception_type === '1') {
                serviceDaysMap[dateStr] = true;
            } else if (exception.exception_type === '2') {
                serviceDaysMap[dateStr] = false;
            }
        });
        
        // Count the service days
        serviceDaysInMonth = Object.values(serviceDaysMap).filter(isServiceDay => isServiceDay).length;
        
        // Now calculate the total kilometers for all deadruns in this shift
        deadruns.forEach(deadrun => {
            // Convert distance from meters to kilometers
            const distanceKm = parseFloat(deadrun.distance) / 1000;
            
            // Each deadrun occurs once per service day
            totalKm += distanceKm * serviceDaysInMonth;
        });
        
        return totalKm;
    }

    // Generate dead-run summary
    generateDeadrunSummary(monthlyStats) {
        if (!this.data.deadruns || !Array.isArray(this.data.deadruns)) {
            return [];
        }

        // Get unique shift IDs
        const shiftIds = [...new Set(this.data.deadruns.map(dr => dr.shiftId))];
        const shiftsById = keyBy(this.data.shifts, 'shift_id');

        return shiftIds.map(shiftId => {
            const monthlyKilometers = monthlyStats.map(month => {
                const monthDate = dayjs(month.month, 'YYYY-MM');
                return {
                    month: month.month,
                    kilometers: this.calculateDeadrunKilometers(shiftId, monthDate)
                };
            });

            // Get dead-run details for this shift
            const shiftDeadruns = this.data.deadruns.filter(dr => dr.shiftId === shiftId);
            const deadrunTypes = [...new Set(shiftDeadruns.map(dr => dr.type))];
            const shift = shiftsById[shiftId];

            return {
                shiftId,
                monthlyKilometers,
                deadrunTypes,
                totalDeadruns: shiftDeadruns.length,
                shiftName: shift?.driver_shift,
                vehicleShift: shift?.vehicle_shift
            };
        });
    }
} 